import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import FocusTrap from 'focus-trap-react';
import {
  Button, IconClose, TextInput, Icon, getTranslation,
} from '@resy/ui-kitchen';
import ABTestWithFallback from '../../../../../ui-kitchen/src/components/Experiments/ABTestWithFallback';
import Variation from '../../../../../ui-kitchen/src/components/Experiments/Variation';
import useExperiment from '../../../../../ui-kitchen/src/components/Experiments/useExperiment';
import { JCE_INTEGRATION_WINERY } from '../../../../../ui-kitchen/src/constants/optimizelyExperiments';

import pinSVG from '../../../../../ui-kitchen/src/svg/icons/ic_location_20x20.svg';

import CitiesList from '../CitiesList';
import './LocationsMenu.scss';

const MAX_CITIES_TO_DISPLAY = 10;
const dedupeLocationsBySlugOrName = (locationList = []) => {
  const seen = new Set();
  return locationList.filter((location) => {
    const key = location.url_slug || location.name?.toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const LocationsMenu = (props) => {
  const {
    allLocations,
    isOpen,
    locations,
    onClose,
    onLocationSelect,
    selectedLocation,
    wineRegions,
    hideWineLocations,
  } = props;

  const [isWineRegionsEnabled] = useExperiment(JCE_INTEGRATION_WINERY);

  const [state, setState] = React.useState({
    search: '',
    filteredLocations: [],
    interacted: false,
    activeTab: 'cities',
  });

  useEffect(() => {
    if (!isOpen && (state.interacted || state.activeTab !== 'cities')) {
      setState({
        ...state,
        search: '',
        interacted: false,
        activeTab: 'cities',
      });
    }
  }, [isOpen]);

  const onChange = (e) => {
    const search = e.locationSearch;
    const searchableLocations = dedupeLocationsBySlugOrName([
      ...(allLocations || []),
      ...wineRegions,
    ]);

    if (search.length === 0) {
      setState({
        ...state,
        filteredLocations: [],
        search,
      });
    } else if (search.trim() !== '') {
      const filtered = searchableLocations.filter(
        (location) => {
          if (location.name.toLowerCase()
            .indexOf(search.trim().toLowerCase()) > -1) {
            return location;
          }
          return null;
        }
      );
      setState({
        ...state,
        filteredLocations: filtered.slice(0, MAX_CITIES_TO_DISPLAY),
        search,
        interacted: true,
      });
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      document.querySelector('.CitiesList__list li:last-child').focus();
    } else if (e.key === 'ArrowDown') {
      document.querySelector('.CitiesList__list li:first-child').focus();
    } else if (e.key === 'Escape' && e.value !== '') {
      setState({ ...state, search: '' });
    }
  };

  const onTabChange = (activeTab) => {
    setState({
      ...state,
      activeTab,
    });
  };

  const hasSearchTerm = state.search.trim() !== '';
  const hasSearchResults = Boolean(state.filteredLocations?.length);
  const showPopularCities = !hasSearchTerm || !hasSearchResults;
  const showWineRegionsTabs = !hideWineLocations && isWineRegionsEnabled && showPopularCities;

  const controlPlaceholder = getTranslation({ translationKey: 'location_search_placeholder', fallback: 'Search all locations...' });
  const featurePlaceholder = getTranslation({ translationKey: 'location_search_placeholder_experiment', fallback: 'Search for a location' });

  const renderTextInputExperiment = (placeholder) => (
    <TextInput
      autocomplete="off"
      ariaAutocomplete="list"
      ariaControls="cities-list"
      name="locationSearch"
      placeholder={placeholder}
      onChange={onChange}
      onKeyDown={onKeyDown}
      value={state.search}
      ariaLabel={getTranslation({
        translationKey: 'search',
        fallback: 'Search',
      })}
    />
  );

  const renderLocationList = () => (
    <CitiesList
      onLocationSelect={onLocationSelect}
      searchVal={state.search}
      popular={showPopularCities}
      locations={showPopularCities ? locations : state.filteredLocations}
      selectedLocationName={selectedLocation}
      tabs={showWineRegionsTabs ? [
        { id: 'cities', label: 'Popular Cities', locations },
        { id: 'wine-regions', label: 'Wine Regions', locations: wineRegions },
      ] : null}
      activeTab={state.activeTab}
      onTabChange={onTabChange}
      tabsAriaLabel="Location categories"
      showWineRegions={isWineRegionsEnabled}
    />
  );

  return (
    <FocusTrap
      active={isOpen}
      focusTrapOptions={{
        allowOutsideClick: true,
        clickOutsideDeactivates: true,
      }}
    >
      <div
        className={cx('LocationsMenu', {
          'LocationsMenu--open': isOpen,
        })}
      >
        <div className="LocationsMenu__search-container">
          <Icon component={pinSVG} iconClass="pin" />

          <ABTestWithFallback flagKey="location_search_copy" fallbackKey="control" variationKeyList={['control', 'feature']}>
            <Variation variationKey="control">
              {renderTextInputExperiment(controlPlaceholder)}
            </Variation>
            <Variation variationKey="feature">
              {renderTextInputExperiment(featurePlaceholder)}
            </Variation>
          </ABTestWithFallback>

          <Button
            className="LocationsMenu__close-container"
            onClick={onClose}
            variant="close"
            aria-label={getTranslation({
              translationKey: 'close_menu',
              fallback: 'Close menu',
            })}
          >
            <IconClose background={false} />
          </Button>
        </div>
        {renderLocationList()}
      </div>
    </FocusTrap>
  );
};

LocationsMenu.propTypes = {
  allLocations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isOpen: PropTypes.bool.isRequired,
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onLocationSelect: PropTypes.func.isRequired,
  selectedLocation: PropTypes.string.isRequired,
  wineRegions: PropTypes.arrayOf(PropTypes.shape({})),
  hideWineLocations: PropTypes.bool,
};

LocationsMenu.defaultProps = {
  wineRegions: [],
  hideWineLocations: false,
};

const mapStateToProps = ({ resyLocations }) => ({
  allLocations: resyLocations.web,
  wineRegions: resyLocations.wineRegions,
});

LocationsMenu.displayName = 'LocationsMenu';

export { LocationsMenu as ComponentUnderTest };
export default connect(mapStateToProps)(LocationsMenu);
